import { Button, Menu } from '@mantine/core';
import {
  IconChevronDown,
  IconExclamationCircle,
  IconPlayerPause,
  IconPlayerPlay,
  IconWallet,
} from '@tabler/icons-react';
import { useGetDebtor } from 'api';
import { useDebtorRecoveryActions } from 'app/Private/Clients/hooks/use-debtor-recovery-actions';
import { iconProps } from 'components/icon-props';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useHistory } from 'react-router';
import { treatClient } from 'shared/serializer';
import type { Debtor } from 'types/store/client-state';

type MailDetailQuickActionsProps = {
  debtorId: string | number;
};

export const MailDetailQuickActions = ({ debtorId }: MailDetailQuickActionsProps) => {
  const { data, refetch } = useGetDebtor(debtorId);

  const rawDebtor = data?.data;
  if (!rawDebtor) return null;

  return <QuickActionsMenu debtor={treatClient(rawDebtor)} onSuccess={refetch} />;
};

type QuickActionsMenuProps = {
  debtor: Debtor;
  onSuccess: () => void;
};

const QuickActionsMenu = ({ debtor, onSuccess }: QuickActionsMenuProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const company = useCompanyConfiguration();
  const { isPaused, isDisputed, togglePause, toggleDispute } = useDebtorRecoveryActions({
    debtor,
    onSuccess,
  });

  return (
    <Menu withArrow position="bottom-end">
      <Menu.Target>
        <Button variant="default" size="xs" rightSection={<IconChevronDown size={14} />}>
          {t(i18nKeys.NAV.QUICK_ACTION.LABEL)}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={
            isPaused ? <IconPlayerPlay {...iconProps()} /> : <IconPlayerPause {...iconProps()} />
          }
          disabled={isDisputed}
          onClick={togglePause}
        >
          {isPaused
            ? t(i18nKeys.CLIENT.DETAIL.UNPAUSE_DEBTOR_RECOVERY)
            : t(i18nKeys.CLIENT.DETAIL.PAUSE_DEBTOR_RECOVERY)}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconExclamationCircle {...iconProps()} />}
          disabled={isPaused}
          onClick={toggleDispute}
        >
          {isDisputed ? t(i18nKeys.UNDISPUTED) : t(i18nKeys.MARK_AS_DISPUTED)}
        </Menu.Item>
        {company.package.can_use_payment_plans && (
          <Menu.Item
            leftSection={<IconWallet {...iconProps()} />}
            onClick={() => history.push(`/payment-plans/new?debtor_id=${debtor.id}`)}
          >
            {t(i18nKeys.PAYMENT_PLAN.TITLE.ONE)}
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
